/**
 * Admin-Team-Verwaltung: CRUD für Kanzlei-Mitarbeiter.
 * Nur sichtbar für admin-Rolle.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trash2, Plus, Users, AlertCircle, Loader2, KeyRound, Mail, ShieldCheck, ChevronDown, Check, X } from 'lucide-react';
import { PasswordDialog } from '../components/PasswordDialog';
import { api, useAuth } from '../context/AuthContext';

const ROLES = [
  { key: 'kanzlei_mitarbeiter', label: 'Mitarbeiter (Admin-Bereich, ohne Löschen)' },
  { key: 'admin', label: 'Admin (Vollzugriff + Team-Verwaltung)' },
  { key: 'freelancer', label: 'Freelancer (nur zugewiesene Akten, keine Zahlungen)' },
];
const VERSCHWIEGENHEIT = 'Die schriftliche Verpflichtung zur Verschwiegenheit mit Belehrung über die strafrechtlichen Folgen (§ 203 Abs. 4 StGB, § 43e BRAO) liegt vor.';

const RECHTE = {
  kanzlei_mitarbeiter: {
    cls: 'bg-sky-50 text-sky-900 border-sky-200',
    darf: ['Team-Portal & Akten', 'Admin-Bereich: Anfragen, Erfassungen, Rückrufe, Termine', 'Raten & Akonto anlegen/senden', 'Mandatsunterlagen erstellen & gegenzeichnen', 'Eigene Chat-Nachrichten löschen'],
    nicht: ['Löschen (einzeln & Sammel-Löschen)', 'Team-/Benutzerverwaltung', 'Passwörter zurücksetzen & Rollen ändern', 'Fremde Chat-Nachrichten löschen'],
  },
  admin: {
    cls: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    darf: ['Alles, was Mitarbeiter dürfen', 'Löschen (einzeln & Sammel-Löschen)', 'Team-Verwaltung: anlegen, Rollen, Abteilungen, Passwörter', 'Alle Chat-Nachrichten löschen'],
    nicht: [],
  },
};

const RechteBadge = ({ u }) => {
  const [open, setOpen] = useState(false);
  const r = RECHTE[u.role] || RECHTE.kanzlei_mitarbeiter;
  const kurz = u.role === 'admin' ? 'Vollzugriff inkl. Löschen & Team' : 'Admin-Bereich · kein Löschen · keine Benutzerverwaltung';
  return (
    <div className="mt-2" data-testid={`team-rechte-${u.user_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border ${r.cls}`} data-testid={`team-rechte-toggle-${u.user_id}`} aria-expanded={open}>
        <ShieldCheck className="w-3 h-3" /> Rechte: {kurz} <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 grid sm:grid-cols-2 gap-3 text-[11px] text-[#34536b] bg-[#f9fbfc] border border-gray-100 rounded-sm p-3" data-testid={`team-rechte-details-${u.user_id}`}>
          <ul className="space-y-1">{r.darf.map((t) => <li key={t} className="flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" /> {t}</li>)}</ul>
          <ul className="space-y-1">{r.nicht.length ? r.nicht.map((t) => <li key={t} className="flex items-start gap-1.5"><X className="w-3 h-3 mt-0.5 text-rose-500 shrink-0" /> {t}</li>) : <li className="text-[#9aa5b1]">Keine Einschränkungen</li>}</ul>
        </div>
      )}
    </div>
  );
};

const ABTEILUNGEN = [
  { key: 'rechtsberatung', label: 'Rechtsberatung', cls: 'bg-[#1B3A5C] text-white' },
  { key: 'steuerberatung', label: 'Steuerberatung', cls: 'bg-[#b8893d] text-white' },
  { key: 'beide', label: 'Recht & Steuern', cls: 'bg-gray-100 text-[#34536b]' },
];

const BenachrichtigungsEmail = ({ u, onSaved }) => {
  const [v, setV] = useState(u.benachrichtigungs_email || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setV(u.benachrichtigungs_email || ''); }, [u.benachrichtigungs_email]);
  const dirty = v.trim().toLowerCase() !== (u.benachrichtigungs_email || '');
  const patch = async (body) => {
    setBusy(true); setErr('');
    try { await api.patch(`/admin/team/${u.user_id}`, body); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1.5 space-y-1" data-testid={`team-notify-${u.user_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[10px] tracking-wider uppercase text-[#7a8a99] flex items-center gap-1" title="Weiterleitungen (Rückruf-Zuweisungen, Termin-Kopien) gehen an diese Adresse statt an die Login-E-Mail"><Mail className="w-3 h-3" /> Weiterleitung an</label>
        <input type="email" value={v} onChange={(e) => setV(e.target.value)} placeholder={u.email} className="text-[11px] px-2 py-0.5 border border-gray-200 bg-white w-60 focus:border-[#b8893d] focus:outline-none" data-testid={`team-notify-input-${u.user_id}`} />
        {dirty && <button type="button" onClick={() => patch({ benachrichtigungs_email: v.trim() })} disabled={busy} className="text-[10px] tracking-wider uppercase px-2 py-0.5 bg-[#1B3A5C] text-white disabled:opacity-50" data-testid={`team-notify-save-${u.user_id}`}>{busy ? '…' : 'Speichern'}</button>}
        {err && <span className="text-[10px] text-rose-600">{err}</span>}
      </div>
      <label className="flex items-center gap-2 text-[11px] text-[#34536b] cursor-pointer" title="Alle Kanzlei-Sammelmails (Ratenanfragen, Akontozahlungen, Rückrufe, Notfälle, Honorarrechner …) zusätzlich an die Weiterleitungsadresse bzw. Login-E-Mail senden">
        <input type="checkbox" className="w-3.5 h-3.5 accent-[#b8893d]" checked={!!u.sammelmails} disabled={busy} onChange={(e) => patch({ sammelmails: e.target.checked })} data-testid={`team-sammelmails-${u.user_id}`} />
        Kanzlei-Sammelmails zusätzlich an {u.benachrichtigungs_email || u.email}
      </label>
    </div>
  );
};

const AdminTeam = ({ eingebettet = false }) => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pwUser, setPwUser] = useState(null);
  const [gebiete, setGebiete] = useState([]);
  useEffect(() => { api.get('/admin/rueckrufe/rechtsgebiete').then((r) => setGebiete(r.data.items || [])).catch(() => setGebiete([])); }, []);
  const toggleGebiet = async (u, key) => {
    const next = (u.assigned_rechtsgebiete || []).includes(key) ? (u.assigned_rechtsgebiete || []).filter((g) => g !== key) : [...(u.assigned_rechtsgebiete || []), key];
    try { await api.patch(`/admin/team/${u.user_id}`, { assigned_rechtsgebiete: next }); loadTeam(); } catch (e) { setError('Fachgebiete konnten nicht gespeichert werden'); }
  };
  const [form, setForm] = useState({ email: '', name: '', role: 'kanzlei_mitarbeiter', abteilung: 'beide', password: '', assigned_rechtsgebiete: [] });
  const updateAbteilung = async (uid, abteilung) => {
    try { await api.patch(`/admin/team/${uid}`, { abteilung }); await loadTeam(); }
    catch (e) { setError(e?.response?.data?.detail || 'Abteilung konnte nicht gespeichert werden'); }
  };
  const [submitting, setSubmitting] = useState(false);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/team');
      setTeam(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) { navigate('/login?next=/admin/team'); return; }
    if (authUser.role !== 'admin') { if (!eingebettet) navigate('/'); return; }
    loadTeam();
  }, [authUser, authLoading, navigate, loadTeam]);

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await api.post('/admin/team', form);
      setForm({ email: '', name: '', role: 'kanzlei_mitarbeiter', abteilung: 'beide', password: '', assigned_rechtsgebiete: [] });
      await loadTeam();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Anlegen');
    } finally { setSubmitting(false); }
  };

  const remove = async (uid) => {
    if (!window.confirm('Diesen Mitarbeiter wirklich löschen?')) return;
    try {
      await api.delete(`/admin/team/${uid}`);
      await loadTeam();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const updateRole = async (uid, newRole) => {
    try {
      const extra = newRole === 'freelancer' ? { verschwiegenheit_bestaetigt: window.confirm(`${VERSCHWIEGENHEIT}\n\nBestätigen?`) } : {};
      await api.patch(`/admin/team/${uid}`, { role: newRole, ...extra });
      await loadTeam();
    } catch (e) { setError(e?.response?.data?.detail || 'Fehler'); }
  };

  if (authLoading || !authUser) return <div className="min-h-screen flex items-center justify-center text-[#34536b]">Lade…</div>;

  return (
    <div className={eingebettet ? '' : 'min-h-screen bg-white'}>
      {!eingebettet && <Header />}
      {!eingebettet && <section className="bg-[#1B3A5C] text-white pt-28 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Startseite
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-[#b8893d]" />
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">Team-Verwaltung</h1>
          </div>
          <p className="text-white/70 text-sm">Mitarbeiter-Zugänge für die Kanzlei verwalten</p>
        </div>
      </section>}

      <main className={eingebettet ? 'p-4' : 'bg-[#f5f8fa] py-10'}>
        <div className={eingebettet ? '' : 'max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6'}>
          {!eingebettet && <aside className="lg:col-span-1 bg-white p-6 border border-gray-200">
            <h2 className="font-serif text-lg text-[#1B3A5C] mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Neuer Mitarbeiter
            </h2>
            <form onSubmit={create} className="space-y-3 text-sm">
              <input type="email" placeholder="E-Mail" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="team-email" className="w-full px-3 py-2 border border-gray-300" />
              <input type="text" placeholder="Vor- und Nachname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="team-name" className="w-full px-3 py-2 border border-gray-300" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role"
                      className="w-full px-3 py-2 border border-gray-300 bg-white">
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              {form.role === 'freelancer' && (
                <label className="flex items-start gap-2 text-xs text-[#1B3A5C] md:col-span-2" data-testid="team-verschwiegenheit">
                  <input type="checkbox" checked={!!form.verschwiegenheit_bestaetigt} onChange={(e) => setForm({ ...form, verschwiegenheit_bestaetigt: e.target.checked })} className="mt-0.5 accent-[#b8893d]" />
                  {VERSCHWIEGENHEIT}
                </label>
              )}
              <select value={form.abteilung} onChange={(e) => setForm({ ...form, abteilung: e.target.value })} data-testid="team-abteilung"
                      className="w-full px-3 py-2 border border-gray-300 bg-white">
                {ABTEILUNGEN.map((a) => <option key={a.key} value={a.key}>Abteilung: {a.label}</option>)}
              </select>
              <input type="password" placeholder="Initial-Passwort (≥8 Zeichen, optional bei Google-Login)" minLength={0} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                     data-testid="team-password" className="w-full px-3 py-2 border border-gray-300" />
              <p className="text-[10px] text-[#34536b] leading-relaxed">
                Existiert die E-Mail bereits (z. B. weil sich der Mitarbeiter schon per Google angemeldet hat), wird das Konto auf die gewählte Team-Rolle <strong>befördert</strong>. Passwort dann optional.
              </p>
              {error && (
                <div className="flex items-center gap-1.5 text-red-700 text-xs bg-red-50 px-2 py-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> {error}
                </div>
              )}
              <Button type="submit" disabled={submitting} data-testid="team-create-btn"
                      className="w-full bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
                ZUGANG ANLEGEN
              </Button>
            </form>
          </aside>}

          <section className="lg:col-span-2 bg-white border border-gray-200">
            <div className="p-6 border-b border-gray-100">
              <h2 className="font-serif text-lg text-[#1B3A5C]">Team ({team.length})</h2>
            </div>
            {loading ? (
              <div className="text-center py-12 text-[#34536b]">Lade…</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {team.map((u) => (
                  <div key={u.user_id} className="p-5 hover:bg-[#fbfcfd] flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`team-row-${u.user_id}`}>
                    <div>
                      <div className="font-medium text-[#1B3A5C] flex items-center gap-2">
                        {u.name}
                        <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ${(ABTEILUNGEN.find((a) => a.key === (u.abteilung || 'beide')) || ABTEILUNGEN[2]).cls}`} data-testid={`team-abteilung-badge-${u.user_id}`}>
                          {(ABTEILUNGEN.find((a) => a.key === (u.abteilung || 'beide')) || ABTEILUNGEN[2]).label}
                        </span>
                      </div>
                      <div className="text-xs text-[#34536b]">{u.email}</div>
                      <BenachrichtigungsEmail u={u} onSaved={loadTeam} />
                      {!eingebettet && <RechteBadge u={u} />}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {!eingebettet && <select value={u.role} onChange={(e) => updateRole(u.user_id, e.target.value)}
                                className="text-[11px] px-2 py-0.5 border border-gray-200 bg-white">
                          {ROLES.map((r) => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                          ))}
                        </select>}
                        <select value={u.abteilung || 'beide'} onChange={(e) => updateAbteilung(u.user_id, e.target.value)} data-testid={`team-abteilung-${u.user_id}`}
                                className="text-[11px] px-2 py-0.5 border border-gray-200 bg-white">
                          {ABTEILUNGEN.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`team-gebiete-${u.user_id}`}>
                        {gebiete.map((g) => {
                          const on = (u.assigned_rechtsgebiete || []).includes(g.key);
                          return <button key={g.key} type="button" onClick={() => toggleGebiet(u, g.key)} data-testid={`team-gebiet-${u.user_id}-${g.key}`} className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${on ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#7a8a99] border-gray-200 hover:border-[#b8893d]'}`}>{g.label}</button>;
                        })}
                        {(u.assigned_rechtsgebiete || []).length === 0 && <span className="text-[10px] text-[#9aa5b1] self-center">Fachgebiete für automatische Rückruf-Vorschläge wählen</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                    <button type="button" onClick={() => setPwUser(u)} data-testid={`team-password-${u.user_id}`}
                            className="text-[#1B3A5C] hover:text-[#b8893d] text-xs flex items-center gap-1" title="Passwort setzen / zurücksetzen">
                      <KeyRound className="w-3.5 h-3.5" /> Passwort
                    </button>
                    <button type="button" onClick={() => remove(u.user_id)} disabled={u.user_id === authUser.user_id}
                            data-testid={`team-delete-${u.user_id}`}
                            title={u.user_id === authUser.user_id
                              ? 'Sie können Ihren eigenen Account nicht löschen. Bitten Sie einen anderen Admin.'
                              : 'Mitarbeiter-Zugang entfernen'}
                            className="text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs flex items-center gap-1">
                      <Trash2 className="w-3.5 h-3.5" /> {u.user_id === authUser.user_id ? 'Eigener Account' : 'Löschen'}
                    </button>
                    </div>
                  </div>
                ))}
                {team.length === 0 && <p className="text-center py-12 text-[#34536b]">Noch keine Team-Mitglieder angelegt.</p>}
              </div>
            )}
          </section>
        </div>
      </main>

      {pwUser && <PasswordDialog user={pwUser} isSelf={pwUser.user_id === authUser.user_id} onClose={() => setPwUser(null)} onDone={loadTeam} />}
      {!eingebettet && <Footer />}
    </div>
  );
};

export default AdminTeam;
